import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Box
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { baseClient } from '../api/baseClient';
import type { Task } from '../types/Task';

interface TaskListProps {
  dealId: string;
  tasks?: Task[];
}

export default function TaskList({ dealId, tasks: initialTasks }: TaskListProps) {
  const queryClient = useQueryClient();

  // Fetch tasks for the deal if not provided
  const { data: tasks, isLoading, isError } = useQuery({
    queryKey: ['tasks', dealId],
    queryFn: async () => {
      const response = await baseClient.get<Task[]>(`/tasks/?deal_id=${dealId}`);
      return response.data;
    },
    enabled: !initialTasks // Only fetch if tasks were not provided
  });

  // Toggle task completion status
  const toggleTaskMutation = useMutation({
    mutationFn: (task: Task) =>
      baseClient.patch(`/tasks/${task.id}`, {
        is_completed: !task.is_completed,
        status: task.is_completed ? 'pending' : 'completed'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', dealId] });
    }
  });

  // Delete task
  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) =>
      baseClient.delete(`/tasks/${taskId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', dealId] });
    }
  });

  const handleToggleComplete = (task: Task) => {
    toggleTaskMutation.mutate(task);
  };

  const handleDeleteTask = (taskId: string) => {
    deleteTaskMutation.mutate(taskId);
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  if (isError) {
    return <Typography color="error">Error loading tasks</Typography>;
  }

  const displayTasks = initialTasks || tasks;

  if (!displayTasks || displayTasks.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Tasks</Typography>
        <Typography color="textSecondary">No tasks yet. Create one from an insight!</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Tasks</Typography>
      <List>
        {displayTasks.map((task) => (
          <ListItem
            key={task.id}
            dense
            sx={{
              textDecoration: task.is_completed ? 'line-through' : 'none',
              opacity: task.is_completed ? 0.7 : 1
            }}
          >
            <Checkbox
              edge="start"
              checked={task.is_completed}
              onChange={() => handleToggleComplete(task)}
              disabled={toggleTaskMutation.isPending}
            />
            <ListItemText
              primary={task.title}
              secondary={
                <Box component="span">
                  {task.due_date && (
                    <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                      Due: {new Date(task.due_date).toLocaleDateString()}
                    </Typography>
                  )}
                  <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                    Priority: {task.priority}
                  </Typography>
                  <Typography variant="caption" component="span" sx={{ mr: 2 }}>
                    Status: {task.status}
                  </Typography>
                  <Typography variant="caption" component="span">
                    Created: {new Date(task.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                onClick={() => handleDeleteTask(task.id)}
                disabled={deleteTaskMutation.isPending}
              >
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
} 